import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { isModuleEnabledForCompany, ROUTE_TO_MODULE, BRANCH_MODULE_ID, getRoutePermission } from '../config/moduleRegistry';
import { setCanPostChecker } from '../utils/transactionHelper';
import { reportItemAllowed, reportForRoute } from '../utils/reportRights';
import {
  cacheOfflineCredentials,
  verifyOfflineCredentialsDetailed,
  updateCachedContext,
  clearOfflineCredentials
} from '../utils/offlineAuth';

// "Show Two Units" needs Units of Measure on as well. Published on window so
// save enrichment and print templates (non-React) can read it.
const twoUnitsOn = (co) => isModuleEnabledForCompany('master_units', co?.enabledModules)
  && isModuleEnabledForCompany('item_two_units', co?.enabledModules);

const AuthContext = createContext();

// Active company / branch persistence.
//   sessionStorage → PER TAB. Two tabs (e.g. super-admin in one, a user company
//     in the other) keep independent selections and stop overwriting each
//     other's choice mid-session — the root cause of the "No Company" stuck screen.
//   localStorage  → cross-restart MEMORY. A brand-new tab with no session value
//     yet seeds its starting company from here.
// Writes update both; reads prefer THIS tab's own session value.
const readActiveId = (key) => {
  try { return sessionStorage.getItem(key) || localStorage.getItem(key); }
  catch (_) { return null; }
};
const writeActiveId = (key, val) => {
  try { sessionStorage.setItem(key, val); localStorage.setItem(key, val); }
  catch (_) { /* storage unavailable (private mode) — non-fatal */ }
};
const clearActiveId = (key) => {
  try { sessionStorage.removeItem(key); localStorage.removeItem(key); }
  catch (_) { /* non-fatal */ }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeCompany, setActiveCompany] = useState(null);
  const [companyUser, setCompanyUser] = useState(null);  // User's role/permissions in active company
  const [activeFinancialYear, setActiveFinancialYear] = useState(null);  // Active financial year

  // Publish the active financial year id globally (same pattern as the currency
  // globals below). Opening balances are entered PER financial year — the new
  // year's opening balance already contains last year's closing. So any "current
  // balance / current stock" figure MUST be scoped to the active year, otherwise
  // it adds both years' opening balances together and shows exactly double.
  // The stock + party-balance helpers read this to scope their maths.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__AMAL_ACTIVE_FY_ID__ = activeFinancialYear?.id || null;
    }
  }, [activeFinancialYear]);
  const [activeBranch, setActiveBranch] = useState(null);  // Active branch (null if multi-branch disabled)
  const [branches, setBranches] = useState([]);  // All active branches for the company
  const [loading, setLoading] = useState(true);
  // True when the user IS logged in and HAS companies, but none could be loaded
  // (transient read failure / two-tab race). Drives a "Retry" recovery screen
  // instead of the dead "No Company" dashboard that used to force a re-login.
  const [companyLoadFailed, setCompanyLoadFailed] = useState(false);
  const isSigningUp = useRef(false);  // Track signup in progress to avoid race conditions
  // Marks the current session as "logged in via cached offline credentials"
  // (no real Firebase token). When true:
  //   - The onAuthStateChanged null events from Firebase are ignored (we
  //     manually own the state)
  //   - cached-context updates are suppressed (the snapshot was already
  //     restored from cache; rewriting it during the session would be wasteful)
  const isOfflineAuth = useRef(false);
  // Holds { email, password } briefly after a successful online login. The
  // cache-snapshot useEffect picks it up once activeCompany + companyUser
  // etc. are fully loaded, then clears it. This is more reliable than the
  // earlier fire-and-forget approach because it doesn't race with the user
  // closing wifi mid-cache.
  const pendingPasswordRef = useRef(null);

  // Sign up function - creates global user profile
  const signup = async (email, password, userData) => {
    try {
      // Mark signup in progress to prevent onAuthStateChanged from interfering
      isSigningUp.current = true;

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Set current user immediately (don't wait for onAuthStateChanged)
      setCurrentUser(user);

      const userProfileData = {
        uid: user.uid,
        email: user.email,
        displayName: userData.displayName,
        phone: userData.phone || '',
        companies: userData.companies || [],  // Array of company IDs user belongs to
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Create global user profile in Firestore
      await setDoc(doc(db, 'users', user.uid), userProfileData);

      // Set user profile immediately to avoid race condition with onAuthStateChanged
      setUserProfile(userProfileData);

      return userCredential;
    } catch (error) {
      console.error('Signup error:', error);
      isSigningUp.current = false;
      throw error;
    }
  };

  // Call this after signup flow completes (after company documents created)
  const completeSignup = () => {
    isSigningUp.current = false;
  };

  // Login function with lastLogin update.
  //
  // Online path (normal): signInWithEmailAndPassword → update lastLogin →
  // in the background, cache credentials + context snapshot for future
  // offline logins on this device.
  //
  // Offline path (fallback): when Firebase login fails because there's no
  // network OR the cached Firebase token has been cleared (e.g. previous
  // logout while offline), check our local credential cache. If we have a
  // hash on file for this email and the password matches, restore AuthContext
  // state from the cached snapshot — user gets into the app without internet.
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      isOfflineAuth.current = false;

      // Stash the password for the cache useEffect to pick up once
      // AuthContext state is fully loaded (onAuthStateChanged → switchCompany
      // runs after we return). This is more reliable than caching here —
      // here, React state isn't ready yet AND closing wifi mid-cache would
      // silently lose the credentials.
      pendingPasswordRef.current = { email, password };

      // Update lastLogin in global user profile
      try {
        await updateDoc(doc(db, 'users', userCredential.user.uid), {
          lastLogin: new Date().toISOString()
        });
      } catch (e) { /* non-fatal — Firestore offline cache will catch up */ }

      return userCredential;
    } catch (firebaseErr) {
      // Treat network-level failures as "try offline fallback". Real credential
      // failures (auth/wrong-password, auth/user-not-found, auth/user-disabled)
      // come back from Firebase only when ONLINE — they reach the user as-is.
      const isNetworkFail =
        !navigator.onLine ||
        firebaseErr?.code === 'auth/network-request-failed' ||
        firebaseErr?.code === 'auth/timeout';

      if (isNetworkFail) {
        const verifyResult = await verifyOfflineCredentialsDetailed(email, password);
        if (verifyResult.snapshot) {
          const snapshot = verifyResult.snapshot;
          // Restore AuthContext state from the cached snapshot. No Firestore
          // reads; no Firebase Auth call. This is purely local.
          isOfflineAuth.current = true;
          const synthUser = {
            uid: snapshot.user.uid,
            email: snapshot.user.email,
            displayName: snapshot.user.displayName || null,
            _offline: true
          };
          setCurrentUser(synthUser);
          setUserProfile(snapshot.userProfile);
          setActiveCompany(snapshot.activeCompany);
          setCompanyUser(snapshot.companyUser);
          setActiveFinancialYear(snapshot.activeFinancialYear);
          setBranches(snapshot.branches || []);
          setActiveBranch(snapshot.activeBranch);
          if (snapshot.activeCompanyId) {
            writeActiveId('activeCompanyId', snapshot.activeCompanyId);
          }

          // Re-establish window currency globals so utility code formats numbers correctly
          if (typeof window !== 'undefined' && snapshot.activeCompany) {
            window.__AMAL_CURRENCY_SYMBOL__ = snapshot.activeCompany?.currencySymbol || 'Rs.';
            const dp = snapshot.activeCompany?.decimalPlaces ?? snapshot.activeCompany?.currency?.decimalPlaces;
            window.__AMAL_DECIMAL_PLACES__ = Number.isFinite(Number(dp)) ? Number(dp) : 2;
            window.__AMAL_DATE_FORMAT__ = snapshot.activeCompany?.dateFormat || 'dd MMM yyyy';
            window.__AMAL_WHT_INCLUSIVE__ = snapshot.activeCompany?.whtOnInclusiveTax === true;
            window.__AMAL_TWO_UNITS__ = twoUnitsOn(snapshot.activeCompany);
          }

          // Surface a fake "userCredential"-shape so callers (Login.js) treat
          // this as a successful login.
          return { user: synthUser, _offline: true };
        }
        // Give specific, actionable error per failure mode so the user knows
        // what went wrong (no cached creds vs. wrong password vs. crypto issue).
        let message;
        switch (verifyResult.error) {
          case 'no-cached-credentials-for-email':
            message = 'No offline session is cached for this email on this device. ' +
                      'Connect to the internet and log in once to enable offline login.';
            break;
          case 'wrong-password':
            message = 'Wrong password for offline login. ' +
                      'If you recently changed your password online, log in once online to refresh.';
            break;
          case 'hash-derivation-failed':
            message = 'Offline login is unavailable on this browser ' +
                      '(Web Crypto API not accessible — needs HTTPS).';
            break;
          default:
            message = 'Cannot log in offline. Check the browser console for details.';
        }
        const err = new Error(message);
        err.code = 'auth/offline-' + verifyResult.error;
        throw err;
      }
      console.error('Login error:', firebaseErr);
      throw firebaseErr;
    }
  };

  // Logout function. We DON'T clear the cached offline credentials — that
  // way the same user can log in again offline at the next shift change. To
  // wipe cached creds for a device, use clearOfflineCredentials() from
  // utils/offlineAuth.js directly (e.g. from a "Forget this device" button).
  const logout = async () => {
    try {
      isOfflineAuth.current = false;
      pendingPasswordRef.current = null; // drop any password we were about to cache
      // signOut works even when offline (it just clears local state). If we
      // were in offline-auth mode there's no real Firebase session to sign
      // out of, but calling it is harmless and clears any residual token.
      try { await signOut(auth); } catch (e) { /* offline signOut may throw — ignore */ }
      setCurrentUser(null);
      setUserProfile(null);
      setActiveCompany(null);
      setCompanyUser(null);
      setActiveFinancialYear(null);
      setActiveBranch(null);
      setBranches([]);
      // Clear stored active company and branch
      clearActiveId('activeCompanyId');
      clearActiveId('activeBranchId');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };
  const logoutRef = useRef(null);
  logoutRef.current = logout;

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData);
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Fetch company details
  const fetchCompany = async (companyId) => {
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        return { id: companyDoc.id, ...companyDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching company:', error);
      return null;
    }
  };

  // Fetch active financial year for a company
  const fetchActiveFinancialYear = async (companyId) => {
    try {
      const fyRef = collection(db, 'companies', companyId, 'financialYears');
      const q = query(fyRef, where('isActive', '==', true));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const fyDoc = snapshot.docs[0];
        return { id: fyDoc.id, ...fyDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching active financial year:', error);
      return null;
    }
  };

  // Fetch all active branches for a company
  const fetchBranches = useCallback(async (companyId) => {
    try {
      const branchesRef = collection(db, 'companies', companyId, 'branches');
      const q = query(branchesRef, where('isActive', '==', true));
      const snapshot = await getDocs(q);
      const branchesList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setBranches(branchesList);
      return branchesList;
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
      return [];
    }
  }, []);

  // Re-load branches AND re-sync the ACTIVE branch object. `activeBranch` holds a
  // copy of the branch record, so refreshing the list alone would leave the copy
  // stale — which is why linking a branch to a stock location in Branch Setup
  // used to need a re-login before the POS picked it up.
  const refreshBranches = useCallback(async (companyId) => {
    const cid = companyId || activeCompany?.id;
    if (!cid) return [];
    const list = await fetchBranches(cid);
    setActiveBranch(prev => {
      if (!prev || prev.id === 'all') return prev;
      return list.find(b => b.id === prev.id) || prev;
    });
    return list;
  }, [activeCompany, fetchBranches]);

  // Switch active branch (with access control)
  const switchBranch = useCallback((branchId) => {
    const isAdmin = companyUser?.role === 'admin';

    if (branchId === 'all') {
      if (!isAdmin) return; // Only admin can view all branches
      setActiveBranch({ id: 'all', branchName: 'All Branches', branchCode: 'ALL' });
      writeActiveId('activeBranchId', 'all');
      return;
    }

    const branch = branches.find(b => b.id === branchId);
    if (!branch) return;

    // Non-admin users can only switch to their assigned branches
    if (!isAdmin) {
      const assignedBranches = companyUser?.assignedBranches || [];
      if (!assignedBranches.includes(branchId)) return;
    }

    setActiveBranch(branch);
    writeActiveId('activeBranchId', branchId);
  }, [companyUser, branches]);

  // Check if multi-branch module is enabled
  const isBranchModuleEnabled = useCallback(() => {
    return isModuleEnabledForCompany(BRANCH_MODULE_ID, activeCompany?.enabledModules);
  }, [activeCompany]);

  // Fetch user's role/permissions in a company
  const fetchCompanyUser = async (companyId, userId) => {
    try {
      const companyUserDoc = await getDoc(doc(db, 'companies', companyId, 'users', userId));
      if (companyUserDoc.exists()) {
        const companyUserData = companyUserDoc.data();

        // Also fetch the role details if roleId exists
        if (companyUserData.roleId) {
          try {
            const roleDoc = await getDoc(doc(db, 'companies', companyId, 'roles', companyUserData.roleId));
            if (roleDoc.exists()) {
              companyUserData.roleDetails = roleDoc.data();
            }
          } catch (roleError) {
            console.warn('Could not fetch role details:', roleError);
          }
        }

        // Update lastLogin in company users sub-collection (non-blocking, don't fail if this fails)
        try {
          await updateDoc(doc(db, 'companies', companyId, 'users', userId), {
            lastLogin: new Date().toISOString()
          });
        } catch (updateError) {
          console.warn('Could not update lastLogin:', updateError);
          // Don't fail the whole operation just because lastLogin update failed
        }

        return companyUserData;
      }
      console.error('Company user document does not exist:', companyId, userId);
      return null;
    } catch (error) {
      console.error('Error fetching company user:', error);
      return null;
    }
  };

  // SEC-05 (2026-09-30): file storage now checks a per-login list of companies.
  // If this login's token doesn't carry the company yet (brand-new signup, or
  // the first login after the rollout), ask the server to refresh it and pull a
  // fresh token. Runs in the background — never blocks or fails the login.
  const ensureStorageAccess = async (user, companyId) => {
    try {
      if (!user || !companyId || isOfflineAuth.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      const tok = await user.getIdTokenResult();
      const cids = Array.isArray(tok.claims?.cids) ? tok.claims.cids : [];
      if (tok.claims?.sa === true || cids.includes(companyId)) return;
      await httpsCallable(functions, 'refreshMyStorageAccess')({ companyId });
      await user.getIdToken(true);
    } catch (e) {
      console.warn('Storage access refresh skipped:', e?.message || e);
    }
  };

  // Switch active company
  const switchCompany = async (companyId) => {
    console.log('switchCompany called with companyId:', companyId);

    // Use auth.currentUser directly as React state might not be updated yet
    const user = auth.currentUser || currentUser;
    console.log('Current user:', user?.uid);

    if (!user) {
      console.error('No authenticated user found');
      return false;
    }

    try {
      // Verify user belongs to this company
      console.log('Fetching company user for:', companyId, user.uid);
      const companyUserData = await fetchCompanyUser(companyId, user.uid);
      console.log('Company user data:', companyUserData);

      if (!companyUserData) {
        console.error('Company user not found');
        return false;
      }

      if (!companyUserData.isActive) {
        console.error('Company user is not active');
        return false;
      }

      // Fetch company details
      console.log('Fetching company details for:', companyId);
      const companyData = await fetchCompany(companyId);
      console.log('Company data:', companyData);

      if (!companyData) {
        console.error('Company not found');
        return false;
      }

      // Fetch active financial year
      console.log('Fetching active financial year for:', companyId);
      const fyData = await fetchActiveFinancialYear(companyId);
      console.log('Active financial year:', fyData);

      setActiveCompany(companyData);
      setCompanyUser(companyUserData);
      setActiveFinancialYear(fyData);
      writeActiveId('activeCompanyId', companyId);
      ensureStorageAccess(user, companyId); // background, non-blocking (SEC-05)

      // Set global currency symbol + decimal places so non-React utilities
      // (formatAmount, formatCurrency, print templates, etc.) format consistently.
      // We accept the legacy nested path companyData.currency.decimalPlaces too.
      if (typeof window !== 'undefined') {
        window.__AMAL_CURRENCY_SYMBOL__ = companyData?.currencySymbol || 'Rs.';
        const dp = companyData?.decimalPlaces ?? companyData?.currency?.decimalPlaces;
        window.__AMAL_DECIMAL_PLACES__ = Number.isFinite(Number(dp)) ? Number(dp) : 2;
        window.__AMAL_DATE_FORMAT__ = companyData?.dateFormat || 'dd MMM yyyy';
        window.__AMAL_WHT_INCLUSIVE__ = companyData?.whtOnInclusiveTax === true;
        window.__AMAL_TWO_UNITS__ = twoUnitsOn(companyData);
      }

      // Load branches if multi-branch module is enabled
      // Shadow companies read branches from the real company (always up to date)
      const branchEnabled = isModuleEnabledForCompany(BRANCH_MODULE_ID, companyData?.enabledModules);
      if (branchEnabled) {
        const branchSourceId = companyData?.replicateFrom || companyId;
        const branchesList = await fetchBranches(branchSourceId);
        const storedBranchId = readActiveId('activeBranchId');
        const isAdmin = companyUserData.role === 'admin';
        const userBranches = companyUserData.assignedBranches || [];
        const allowedBranches = isAdmin ? branchesList : branchesList.filter(b => userBranches.includes(b.id));

        if (isAdmin && (!storedBranchId || storedBranchId === 'all')) {
          setActiveBranch({ id: 'all', branchName: 'All Branches', branchCode: 'ALL' });
        } else {
          const found = allowedBranches.find(b => b.id === storedBranchId);
          if (found) {
            setActiveBranch(found);
          } else if (allowedBranches.length > 0) {
            setActiveBranch(allowedBranches[0]);
          } else {
            setActiveBranch(null);
          }
        }
      } else {
        setActiveBranch(null);
        setBranches([]);
        clearActiveId('activeBranchId');
      }

      console.log('Company switched successfully');
      return true;
    } catch (error) {
      console.error('Error switching company:', error);
      return false;
    }
  };

  // Robust company bootstrap used at login. Picks the company to open (this tab's
  // own choice → the remembered one → the first), then actually loads it — with
  // a few retries for transient read blips and a fall-back to any OTHER company
  // the user belongs to. A single failed read must NEVER strand the session at
  // the dead "No Company" screen (which previously forced a manual logout/login).
  const selectAndSwitchCompany = async (userData) => {
    if (!userData || !Array.isArray(userData.companies) || userData.companies.length === 0) {
      // Genuinely has no company (fresh account / dedicated super-admin) — not a failure.
      setCompanyLoadFailed(false);
      return false;
    }
    const storedCompanyId = readActiveId('activeCompanyId');
    const preferred = storedCompanyId && userData.companies.includes(storedCompanyId)
      ? storedCompanyId
      : userData.companies[0];

    const tryLoad = async (cid, attempts) => {
      for (let i = 0; i < attempts; i++) {
        try { if (await switchCompany(cid)) return true; } catch (_) { /* retry */ }
        if (i < attempts - 1) await new Promise((r) => setTimeout(r, 400 * (i + 1)));
      }
      return false;
    };

    // Try the preferred company a few times (transient blips), then fall back to
    // any other company the user belongs to.
    let ok = await tryLoad(preferred, 3);
    if (!ok) {
      for (const cid of userData.companies) {
        if (cid === preferred) continue;
        if (await tryLoad(cid, 1)) { ok = true; break; }
      }
    }
    setCompanyLoadFailed(!ok);
    return ok;
  };

  // "Retry" button on the recovery screen — re-fetch the profile and re-attempt,
  // no logout needed.
  const retryCompanyLoad = async () => {
    const user = auth.currentUser || currentUser;
    if (!user) return false;
    setCompanyLoadFailed(false);
    const userData = await fetchUserProfile(user.uid);
    return selectAndSwitchCompany(userData);
  };

  // Check if user has a specific permission
  // Supports both:
  //   hasPermission('category', 'module', 'action')  — new nested structure
  //   hasPermission('module', 'action')               — legacy flat structure
  //
  // Bypasses (always return true):
  //   - companyUser.role === 'admin'      (company admin, set by UsersRoles page)
  //   - activeCompany.ownerId === uid     (original company creator — anti-lockout fallback)
  const hasPermission = (arg1, arg2, arg3) => {
    if (!companyUser) return false;
    if (companyUser.role === 'admin') return true;
    if (activeCompany?.ownerId && currentUser?.uid && activeCompany.ownerId === currentUser.uid) return true;

    const rolePermissions = companyUser.roleDetails?.permissions || {};
    const userOverrides = companyUser.permissions || {};

    // New nested: hasPermission('transactions', 'purchaseOrder', 'view')
    if (arg3 !== undefined) {
      const catPerms = rolePermissions[arg1];
      // Backward compat: 'dashboard' category was added after some roles were
      // saved. Treat a fully missing dashboard block as "all widgets visible"
      // so existing users don't suddenly lose their dashboard on upgrade.
      // Admins can still hide individual widgets once they re-save the role.
      if (!catPerms) {
        if (arg1 === 'dashboard') return true;
        return false;
      }
      const modPerms = catPerms[arg2];
      if (modPerms === undefined || modPerms === null) {
        // Same fallback when the role has `dashboard: {...}` but the specific
        // widget key wasn't saved (e.g. a widget added in a later release).
        if (arg1 === 'dashboard') return true;
        return false;
      }
      if (typeof modPerms === 'boolean') return modPerms;
      // 'change' (2026-11-04): may change SAVED records. A role with only the Add
      // tick keeps the write right ('edit', so creating works everywhere) plus
      // addOnly: true. Roles saved before this have no addOnly → unchanged.
      if (arg3 === 'change') {
        const ov = userOverrides[arg1]?.[arg2]?.edit;
        const canWrite = ov !== undefined ? ov === true : modPerms.edit === true;
        return canWrite && modPerms.addOnly !== true;
      }
      if (userOverrides[arg1]?.[arg2]?.[arg3] !== undefined) return userOverrides[arg1][arg2][arg3];
      // Backward compat: 'post' was added later. If a role doc has the module
      // but no 'post' key (saved before the column existed), default to true so
      // existing companies don't suddenly lose the ability to post.
      if (arg3 === 'post' && modPerms.post === undefined) return true;
      return modPerms[arg3] === true;
    }

    // Legacy flat: hasPermission('purchases', 'view')
    const modPerms = rolePermissions[arg1];
    if (modPerms === undefined || modPerms === null) return false;
    if (typeof modPerms === 'boolean') return modPerms;
    if (userOverrides[arg1]?.[arg2] !== undefined) return userOverrides[arg1][arg2];
    return modPerms[arg2] === true;
  };

  // Check if user can view a given route path. Combines module-enabled check
  // with the route's declared permission (from moduleRegistry). Routes with no
  // permission requirement are accessible to any company member.
  const canViewRoute = useCallback((routePath) => {
    // Route-level module enablement
    const routeModule = ROUTE_TO_MODULE[routePath];
    if (routeModule && !isModuleEnabledForCompany(routeModule.id, activeCompany?.enabledModules)) return false;

    // Permission requirement
    const perm = getRoutePermission(routePath);
    if (!perm) return true; // No permission required → accessible
    // Inline hasPermission evaluation (avoid stale closure)
    if (!companyUser) return false;
    if (companyUser.role === 'admin') return true;
    if (activeCompany?.ownerId && currentUser?.uid && activeCompany.ownerId === currentUser.uid) return true;
    const rolePermissions = companyUser.roleDetails?.permissions || {};
    const userOverrides = companyUser.permissions || {};
    const catPerms = rolePermissions[perm.category];
    if (!catPerms) return false;
    const modPerms = catPerms[perm.key];
    if (modPerms === undefined || modPerms === null) return false;
    // Reports: the head tick, then the single report's own tick (utils/reportRights.js).
    if (typeof modPerms === 'boolean') {
      if (!modPerms) return false;
      const rep = perm.category === 'reports' ? reportForRoute(routePath) : null;
      return rep ? reportItemAllowed(rep.id, rolePermissions, userOverrides) : true;
    }
    if (userOverrides[perm.category]?.[perm.key]?.view !== undefined) return userOverrides[perm.category][perm.key].view;
    return modPerms.view === true;
  }, [activeCompany, companyUser, currentUser]);

  // Report-wise rights: may this user open this single report? (Its head tick is
  // checked separately, as before.) Admin / owner: always.
  const canOpenReport = useCallback((reportId) => {
    if (!companyUser) return false;
    if (companyUser.role === 'admin') return true;
    if (activeCompany?.ownerId && currentUser?.uid && activeCompany.ownerId === currentUser.uid) return true;
    return reportItemAllowed(reportId, companyUser.roleDetails?.permissions || {}, companyUser.permissions || {});
  }, [activeCompany, companyUser, currentUser]);

  // Check if a module is enabled for the active company. (SAAS-02: the old
  // "Try once" temporary grants no longer switch a module on.)
  const isModuleEnabled = useCallback((moduleId) => (
    isModuleEnabledForCompany(moduleId, activeCompany?.enabledModules)
  ), [activeCompany]);

  // Check if a route's module is enabled for the active company
  const isRouteEnabled = useCallback((routePath) => {
    const mod = ROUTE_TO_MODULE[routePath];
    if (!mod) return true; // Unknown routes are always allowed
    return isModuleEnabledForCompany(mod.id, activeCompany?.enabledModules);
  }, [activeCompany]);

  // Persistence is now configured at construction time in config/firebase.js
  // (initializeAuth with indexedDBLocalPersistence) — no race condition, no
  // useEffect needed. Tabs share auth state via IndexedDB + BroadcastChannel.

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // If we're in offline-auth mode (logged in via cached credentials with
      // no real Firebase token), Firebase will fire null events. Ignoring
      // them keeps the offline session alive until explicit logout.
      if (!user && isOfflineAuth.current) {
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      if (user) {
        // A real Firebase login just landed — clear any prior offline flag
        isOfflineAuth.current = false;

        // Skip company loading if signup is in progress (signup handles this explicitly)
        if (isSigningUp.current) {
          setLoading(false);
          return;
        }

        const userData = await fetchUserProfile(user.uid);

        // Load the active company robustly (retry + fall-back + recovery flag)
        // so a transient read failure or a two-tab race can't strand us at the
        // dead "No Company" screen.
        await selectAndSwitchCompany(userData);
      } else {
        setUserProfile(null);
        setActiveCompany(null);
        setCompanyUser(null);
        setActiveFinancialYear(null);
        setActiveBranch(null);
        setBranches([]);
        setCompanyLoadFailed(false);
      }

      setLoading(false);
    });

    return unsubscribe;
    // Intentionally once-only: the auth subscription must not be torn down and
    // re-created on every render. The handlers it calls use auth.currentUser +
    // state setters, so the first-render closure is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cache snapshot for offline login. Two responsibilities:
  //   1. After a fresh online login (pendingPasswordRef.current is set) →
  //      compute the PBKDF2 hash and write the full credential entry. Then
  //      clear the password ref so it doesn't sit in memory longer than needed.
  //   2. Any subsequent state change while online (user switches branch /
  //      activeFY refreshes / role changes) → update the cached snapshot
  //      without re-hashing, so next offline-login lands in the latest state.
  //
  // No-op when in offline-auth mode (nothing to refresh — the snapshot WAS
  // the source).
  useEffect(() => {
    if (!currentUser?.email || isOfflineAuth.current) return;
    if (!userProfile || !activeCompany) return; // wait for full state load

    const snapshot = {
      user: {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: userProfile?.displayName || null
      },
      userProfile,
      activeCompanyId: activeCompany?.id || null,
      activeCompany,
      companyUser,
      activeFinancialYear,
      branches,
      activeBranch
    };

    const pending = pendingPasswordRef.current;
    if (pending && pending.email.toLowerCase() === currentUser.email.toLowerCase()) {
      // Fresh login — derive hash + write full credential record
      cacheOfflineCredentials(pending.email, pending.password, snapshot)
        .then(() => {
          pendingPasswordRef.current = null; // drop the password from memory
          console.log('[AuthContext] offline credentials cached for', pending.email);
        })
        .catch(err => console.warn('[AuthContext] credential cache failed:', err));
    } else if (navigator.onLine) {
      // Existing user; snapshot refresh only (no hash work)
      updateCachedContext(currentUser.email, snapshot);
    }
  }, [currentUser, userProfile, activeCompany, companyUser, activeFinancialYear, branches, activeBranch]);

  // Live refresh: keep userProfile in sync with Firestore so display name /
  // phone updates (e.g. from the Users & Roles page) propagate to the top-right
  // header without requiring a logout.
  useEffect(() => {
    if (!currentUser?.uid) return;
    const userDocRef = doc(db, 'users', currentUser.uid);
    // Company admin set a new password after this sign-in → sign out here and
    // forget the old password for offline login on this device.
    const signOutIfPasswordChanged = async (changedAt) => {
      const u = auth.currentUser;
      const changed = Date.parse(changedAt);
      if (!u || u.uid !== currentUser.uid || typeof u.getIdTokenResult !== 'function' || !Number.isFinite(changed)) return;
      try {
        const signedIn = Date.parse((await u.getIdTokenResult()).authTime);
        if (Number.isFinite(signedIn) && signedIn < changed - 1000) {
          clearOfflineCredentials(u.email);
          await logoutRef.current?.();
        }
      } catch (e) { /* once sign-in is revoked Firebase signs out by itself */ }
    };
    const unsub = onSnapshot(userDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setUserProfile(data);
        if (data.passwordChangedAt && !isOfflineAuth.current) signOutIfPasswordChanged(data.passwordChangedAt);
      }
    }, (err) => console.warn('userProfile listener error:', err));
    return unsub;
  }, [currentUser?.uid]);

  // Live refresh: when companyUser's role/permissions change in Firestore
  // (e.g. company admin toggles a permission), update companyUser in real time
  // so the UI reflects the change on next navigation without requiring logout.
  useEffect(() => {
    if (!currentUser?.uid || !activeCompany?.id) return;
    const userDocRef = doc(db, 'companies', activeCompany.id, 'users', currentUser.uid);
    const unsub = onSnapshot(userDocRef, async (snap) => {
      if (!snap.exists()) return;
      const next = snap.data();
      if (next.roleId) {
        try {
          const roleDoc = await getDoc(doc(db, 'companies', activeCompany.id, 'roles', next.roleId));
          if (roleDoc.exists()) next.roleDetails = roleDoc.data();
        } catch (err) {
          console.warn('Could not refresh role details:', err);
        }
      }
      setCompanyUser(next);
    }, (err) => console.warn('companyUser listener error:', err));
    return unsub;
  }, [currentUser?.uid, activeCompany?.id]);

  // Live refresh: also listen to the role document itself, so when admin edits
  // role permissions (without reassigning the user), the user picks up the change.
  useEffect(() => {
    if (!activeCompany?.id || !companyUser?.roleId) return;
    const roleDocRef = doc(db, 'companies', activeCompany.id, 'roles', companyUser.roleId);
    const unsub = onSnapshot(roleDocRef, (snap) => {
      if (!snap.exists()) return;
      setCompanyUser(prev => prev ? { ...prev, roleDetails: snap.data() } : prev);
    }, (err) => console.warn('role listener error:', err));
    return unsub;
  }, [activeCompany?.id, companyUser?.roleId]);

  // Live refresh: listen to the company doc so enabledModules / currency / settings
  // changes from Super Admin propagate immediately without requiring a re-login.
  useEffect(() => {
    const companyId = activeCompany?.id;
    if (!companyId) return;
    const companyDocRef = doc(db, 'companies', companyId);
    const unsub = onSnapshot(companyDocRef, (snap) => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      setActiveCompany(data);
      if (typeof window !== 'undefined') {
        window.__AMAL_CURRENCY_SYMBOL__ = data?.currencySymbol || 'Rs.';
        const dp = data?.decimalPlaces ?? data?.currency?.decimalPlaces;
        window.__AMAL_DECIMAL_PLACES__ = Number.isFinite(Number(dp)) ? Number(dp) : 2;
        window.__AMAL_DATE_FORMAT__ = data?.dateFormat || 'dd MMM yyyy';
        window.__AMAL_WHT_INCLUSIVE__ = data?.whtOnInclusiveTax === true;
        window.__AMAL_TWO_UNITS__ = twoUnitsOn(data);
      }
    }, (err) => console.warn('activeCompany listener error:', err));
    return unsub;
  }, [activeCompany?.id]);

  // Refresh active financial year (call this after changing active year in settings)
  const refreshActiveFinancialYear = async () => {
    if (activeCompany?.id) {
      const fyData = await fetchActiveFinancialYear(activeCompany.id);
      setActiveFinancialYear(fyData);
      return fyData;
    }
    return null;
  };

  const value = {
    currentUser,
    userProfile,
    activeCompany,
    companyUser,
    activeFinancialYear,
    activeBranch,
    branches,
    signup,
    completeSignup,
    login,
    logout,
    fetchUserProfile,
    fetchCompany,
    switchCompany,
    switchBranch,
    fetchBranches,
    refreshBranches,
    isBranchModuleEnabled,
    hasPermission,
    canViewRoute,
    canOpenReport,
    isModuleEnabled,
    isRouteEnabled,
    refreshActiveFinancialYear,
    companyLoadFailed,
    retryCompanyLoad,
    loading
  };

  // Register the post-permission checker with transactionHelper so the central
  // create/update guards can enforce Post/Approve rights without prop-drilling.
  useEffect(() => {
    setCanPostChecker((cat, key) => hasPermission(cat, key, 'post'));
  });

  // Show a recovery screen ONLY when logged in with companies that all failed to
  // load — never for a legit no-company account (companyLoadFailed stays false
  // there). This replaces the dead "No Company" dashboard that forced a re-login.
  const showCompanyRecovery = !loading && !!currentUser && !activeCompany && companyLoadFailed;

  return (
    <AuthContext.Provider value={value}>
      {!loading && (showCompanyRecovery ? (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#7a1220' }}>Couldn&apos;t load your company</div>
          <div style={{ fontSize: 14, color: '#555', maxWidth: 440, lineHeight: 1.5 }}>
            You&apos;re logged in, but your company data didn&apos;t load — usually a brief network hiccup or having the app open in another tab. Just try again; no need to log out.
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <button onClick={retryCompanyLoad} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#7a1220', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Retry</button>
            <button onClick={logout} style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', color: '#333', cursor: 'pointer', fontSize: 14 }}>Log out</button>
          </div>
        </div>
      ) : children)}
    </AuthContext.Provider>
  );
};
